import React, { useCallback, useContext, useMemo, useState } from 'react'
import { get, isEmpty, set } from 'lodash'
import cn from 'classnames'

import FilterBarContext from '../../context'

import Search from '../../../SearchItems'
import Fallback from '../FallbackComponent'
import ShowMore from '../../components/ShowMore'
import SelectAllCheckbox from '../../components/SelectAllCheckbox'
import { AccordionButton, SectionDescription } from './components'

import {
  componentsMap,
  ITEMS_OFFSET,
  INITIAL_ITEMS_COUNT
} from '../../constants'
import {
  groupSectionItems,
  isEveryElementCheckboxType,
  isStringMatching,
  isElementMatching,
  updateRecursevely,
  buildPathToValue,
  shouldBeVisible,
  findSectionElement,
  buildNewSectionRecursevely
} from '../../utils'

import './index.scss'

const AccordionItem = ({
  t,
  filters,
  title,
  hierarchy,
  visibility,
  type = 'checkbox', // default ??
  depth,
  onChange,
  description,
  serverSideT,
  elements = [],
  props = {},
  expanded: expandedProp = false
}) => {
  const [expanded, setExpanded] = useState(expandedProp)
  const [searchText, setSearchText] = useState('')
  const [visibleItemsCount, setVisibleItemsCount] =
    useState(INITIAL_ITEMS_COUNT)

  const FilterComponent = componentsMap[type] || Fallback

  const isItemVisible = useMemo(() => {
    if (!isEmpty(visibility)) {
      return shouldBeVisible(filters, visibility)
    }

    return true
  }, [filters, visibility])

  const onItemClick = useCallback(
    ({ target }) => {
      if (target.getAttribute('class')?.includes('bx--checkbox-label')) return
      setExpanded((x) => !x)
    },
    [setExpanded]
  )

  const onSelectAllClick = useCallback(
    (checked) => {
      // TODO handle new sections
      onChange((filters, newSections) => {
        const clonedFilters = { ...filters }
        const filterValues = get(filters, hierarchy)
        const updatedValues = updateRecursevely(filterValues, checked)

        set(clonedFilters, hierarchy, updatedValues)

        const clonedNewSections = [...newSections]
        const sectionElement = findSectionElement(clonedNewSections, hierarchy)

        buildNewSectionRecursevely(sectionElement, checked)

        return { clonedFilters, clonedNewSections }
      }, hierarchy)
    },
    [onChange, hierarchy]
  )

  const onShowMore = useCallback(
    (e) => {
      e.preventDefault()
      setVisibleItemsCount((x) =>
        elements.length <= x ? INITIAL_ITEMS_COUNT : x + ITEMS_OFFSET
      )
    },
    [elements, setVisibleItemsCount]
  )

  const onSearch = useCallback(
    ({ target: { value } }) => {
      setSearchText(value.toLowerCase())
    },
    [setSearchText]
  )

  const applyChecboxMatcher = (elementFilter = []) => {
    const checkboxElementsList = []

    for (let index = 0; index < elementFilter.length; index++) {
      const item = elementFilter[index]
      // if there is another level
      if (item.elements) {
        const titleMatch = isStringMatching(item.title, searchText) // title of subsection group

        if (titleMatch) {
          // if title match return all elements
          checkboxElementsList.push(item)
          continue
        }
        const newElementsList = applyChecboxMatcher(item.elements)
        if (!isEmpty(newElementsList) /* && !titleMatch */) {
          const newItem = { ...item, elements: newElementsList }
          checkboxElementsList.push(newItem)
        }
      }
      // last level
      else {
        if (isElementMatching({ item, searchText })) {
          checkboxElementsList.push(item)
        }
      }
    }

    return checkboxElementsList
  }

  const visibleItems = useMemo(() => {
    if (type === 'autocomplete') return elements
    if (isEmpty(searchText)) {
      return elements.slice(0, visibleItemsCount)
    }
    if (type === 'checkbox') {
      return applyChecboxMatcher(elements)
    } else {
      // radio and others
      const found = elements.filter((item) => {
        if (item.val || item.label) {
          return isElementMatching({ item, searchText })
        }

        return false
      })
      return found.slice(0, visibleItemsCount)
    }
  }, [elements, searchText, visibleItemsCount, type])

  const showComponentShowMore = useMemo(() => {
    if (type === 'autocomplete') return false
    if (!isEmpty(searchText)) {
      return (
        visibleItems.length > 0 && visibleItems.length >= INITIAL_ITEMS_COUNT
      )
    }

    return elements.length > INITIAL_ITEMS_COUNT && visibleItems.length > 0
  }, [searchText, elements, visibleItems, type])

  const translatedTitle = useMemo(
    () => (!isEmpty(title) ? serverSideT(title) : ''),
    [serverSideT, t]
  )

  if (!isItemVisible) {
    return null
  }
  return (
    <li
      className={cn('accordion-custom__item', { 'no-borders': expandedProp })}
    >
      {expandedProp ? (
        <span className='subsection-title'>{translatedTitle}</span>
      ) : (
        <AccordionButton
          hierarchy={hierarchy}
          title={translatedTitle}
          expanded={expanded}
          onClick={onItemClick}
          checkbox={type === 'checkbox' && depth > 0}
          onCheckboxClick={onSelectAllClick}
        />
      )}

      {expanded && (
        <div className='accordion-custom__content'>
          {props.search && (
            <Search
              id={`search-${hierarchy.join()}`}
              name=''
              value={searchText}
              placeHolderText={t('SEARCH')}
              labelText={t('SEARCH')}
              size='lg'
              type='text'
              onChange={onSearch}
            />
          )}

          {props.withSelectAll && isEveryElementCheckboxType(elements) && (
            <SelectAllCheckbox
              t={t}
              withLabel
              filters={filters}
              hierarchy={hierarchy}
              onCheckboxClick={onSelectAllClick}
            />
          )}

          {groupSectionItems(visibleItems).map(({ type, data }, i) => {
            const key = `${hierarchy.join()}-${i}`

            return type === 'accordion' ? (
              <Accordion
                {...props}
                key={key}
                items={data}
                depth={depth + 1}
                hierarchy={hierarchy}
                onChange={onChange}
              />
            ) : (
              <FilterComponent
                {...props}
                key={key}
                items={data}
                hierarchy={hierarchy}
                onChange={onChange}
                onSelectAllClick={onSelectAllClick}
              />
            )
          })}

          {showComponentShowMore && (
            <ShowMore
              allShown={
                elements.length <= visibleItemsCount ||
                (!isEmpty(searchText) &&
                  visibleItems.length + 1 <= visibleItemsCount)
              }
              onChange={onShowMore}
            />
          )}

          {description && <SectionDescription text={description} />}
        </div>
      )}
    </li>
  )
}

const MemoAccordionItem = React.memo(AccordionItem)

const Accordion = ({ items, hierarchy, onChange, depth = 0 }) => {
  const { t, serverSideT, filters } = useContext(FilterBarContext)

  return (
    <ul className='accordion-custom'>
      {items.map((item) => {
        const { id, key } = item
        const itemHierarchy = buildPathToValue(hierarchy, id ?? key)
        return (
          <MemoAccordionItem
            t={t}
            key={id}
            filters={filters}
            serverSideT={serverSideT}
            hierarchy={itemHierarchy}
            {...item}
            depth={depth}
            onChange={onChange}
          />
        )
      })}
    </ul>
  )
}

// Carbon accordion does not support nested accordions
export default React.memo(Accordion)
